import {closeCurrentDialog, FormSubmitHandler, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {defaultTo, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useSendCustomerEmailMutation} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetServiceCheckInQuery,
  useGetServiceHandOverQuery,
  usePostSendCheckinEmailNotificationMutation,
  usePostSendHandoverEmailNotificationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  type EmailFormValues as FormValues,
  handleApiError,
  toEmailBodyPayload,
  type UploadedCustomerMessageFile,
  type useEmailBodyImageUpload,
} from '@dms/shared';

import {getDigitalCertificateUrl} from '../../../utils/getDigitalCertificateUrl';

interface UseSubmitAftersalesEmailProps {
  serviceCaseId: string;
  serviceOrderId?: string;
  uploadedFiles: UploadedCustomerMessageFile[];
  ensureBodyReadyToSend: ReturnType<typeof useEmailBodyImageUpload>['ensureBodyReadyToSend'];
}

export function useSubmitAftersalesEmail(props: UseSubmitAftersalesEmailProps) {
  const [sendCustomerMessage] = useSendCustomerEmailMutation();
  const [sendCheckinEmail] = usePostSendCheckinEmailNotificationMutation();
  const [sendHandoverEmail] = usePostSendHandoverEmailNotificationMutation();

  const serviceCheckinQuery = useGetServiceCheckInQuery({serviceCaseId: props.serviceCaseId});
  const serviceHandoverQuery = useGetServiceHandOverQuery({serviceCaseId: props.serviceCaseId});
  const {data: currentUser} = useGetCurrentUserInfoQuery();

  const checkinInspectionId = defaultTo('', serviceCheckinQuery.data?.inspectionId?.[0]);
  const handoverInspectionId = defaultTo('', serviceHandoverQuery.data?.inspectionId?.[0]);
  const selectedLang = currentUser?.settings?.language ?? 'en';

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const customerMessageType = data.customerMessageId[0];

    // Only the custom message carries a body; the other two render from a template with no editor.
    if (
      customerMessageType === 'customer-message' &&
      !props.ensureBodyReadyToSend(data.body, blockerContext)
    ) {
      return;
    }

    const attachments = isNotNilOrEmpty(props.uploadedFiles)
      ? props.uploadedFiles.map((file) => ({
          fileId: file.fileId,
          convertToPdf: file.shouldConvertToPdf,
        }))
      : undefined;

    await match(customerMessageType)
      .with('customer-message', () =>
        sendCustomerMessage({
          sendCustomerEmailRequestBody: {
            ...toEmailBodyPayload(data.body),
            subject: data.subject ?? '',
            customerId: data.customerId,
            customerEmailAddress: data.customerEmailAddress,
            copyTo: data.copyTo,
            attachments,
            ...(isNotNil(props.serviceOrderId)
              ? {
                  recordId: props.serviceOrderId,
                  resourceId: EntityResourceIds.serviceOrder,
                  secondaryRecordIds: [
                    {
                      recordId: props.serviceCaseId,
                      resourceId: EntityResourceIds.serviceCase,
                    },
                  ],
                }
              : {
                  recordId: props.serviceCaseId,
                  resourceId: EntityResourceIds.serviceCase,
                }),
            smtpCredentialsId: data.smtpCredentialsId,
          },
        })
          .unwrap()
          .then(() => showNotification.success(i18n.t('entity.customer.labels.successfullySend')))
          .then(closeCurrentDialog)
          .catch(handleApiError)
      )
      .with('service-customer_check_in', () => {
        if (!checkinInspectionId) {
          throw new Error('Missing checkinInspectionId for checkin email');
        }

        return sendCheckinEmail({
          serviceCaseId: props.serviceCaseId,
          body: {
            recipients: [
              {
                email: data.customerEmailAddress,
                customerId: data.customerId,
              },
            ],
            digitalCertificateUrl: getDigitalCertificateUrl(checkinInspectionId, selectedLang),
            attachments: props.uploadedFiles.map((file) => ({
              name: file.fileId,
              url: file?.fileUrl ?? '',
            })),
            smtpCredentialsId: data.smtpCredentialsId,
          },
        })
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('general.notifications.emailSendSuccessfully'))
          )
          .then(closeCurrentDialog)
          .catch(handleApiError);
      })
      .with('service-customer_handover', () => {
        if (!handoverInspectionId) {
          throw new Error('Missing handoverInspectionId for handover email');
        }

        return sendHandoverEmail({
          serviceCaseId: props.serviceCaseId,
          body: {
            recipients: [
              {
                email: data.customerEmailAddress,
                customerId: data.customerId,
              },
            ],
            digitalCertificateUrl: getDigitalCertificateUrl(handoverInspectionId, selectedLang),
            attachments: props.uploadedFiles.map((file) => ({
              name: file.fileId,
              url: file?.fileUrl ?? '',
            })),
            smtpCredentialsId: data.smtpCredentialsId,
          },
        })
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('general.notifications.emailSendSuccessfully'))
          )
          .then(closeCurrentDialog)
          .catch(handleApiError);
      })
      .exhaustive();
  };

  return [handleSubmit];
}
